import clsx from 'clsx';
import { AlertTriangle, ArrowRight, CalendarCheck, CalendarClock, CalendarDays, CalendarRange, CheckCheck, ChevronDown, ChevronUp, CircleCheck, ClipboardList, Contact, LayoutGrid, Megaphone, PieChart, Plus, ReceiptText, Server, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { addDays, addMonths, efficiencyTone, isoWeek, monthEnd, monthStart } from '@pz/core';
import { AreaBars } from '../components/areas';
import { HourTerms, HourTypeBars, useCoverageText } from '../components/hour-types';
import { HBars, PlanBar, WeekBars } from '../components/charts';
import { EntryDialog } from '../components/entry';
import { AnnouncementsPanel, TileGrid, UpcomingPanel, type HomeData, type Tile } from '../components/home';
import { CATEGORY_STYLE, notifyText } from '../components/notifications';
import { Button, Checkbox, ErrorBox, Gauge, IconButton, Loading, Modal, Notice, Panel, Pill, Stat, td, th, useToast } from '../components/ui';
import { d, dLong, dt, h, hh, kw, pct, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { findTab, useNav } from '../lib/nav';

interface WeekPoint {
  weekStart: string;
  bookedMinutes: number;
  billableMinutes: number;
  capacityMinutes: number;
  compensationMinutes: number;
  state: 'under' | 'over' | 'ok' | null;
}

interface Dashboard {
  me: {
    weekStart: string;
    days: {
      date: string;
      bookedMinutes: number;
      billableMinutes: number;
      capacityMinutes: number;
      compensationMinutes: number;
      holiday: string | null;
      absent: boolean;
      absenceKind: string | null;
    }[];
    /** false for people without a contract (externals) */
    hasTarget: boolean;
    thisWeekMinutes: number;
    thisWeekCapacity: number;
    thisWeekCompensation: number;
    lastWeekMinutes: number;
    lastWeekCapacity: number;
    lastWeekOpen: number;
    olderOpen: number;
    rejected: number;
    weeks: WeekPoint[];
    fastTasks: { taskId: string; ref: string; title: string; projectNumber: string; doneOn: string; planShareMinutes: number; ownMinutes: number }[];
  };
  overPlan?: { count: number; tasks: { id: string; ref: string; title: string; projectId: string; projectNumber: string; plannedMinutes: number; bookedMinutes: number }[] };
  approvals?: { l1: number; final: number; total: number };
  budgetAlerts?: { id: string; number: string; name: string; bookedMinutes: number; budgetMinutes: number }[];
  formaWarnings?: {
    projects: { id: string; number: string; name: string; state: string }[];
    tasks: { id: string; ref: string; title: string; state: string; projectNumber: string; projectId: string }[];
  };
  billing?: { draftDocs: number; unbilledMinutes: number };
  system?: { syncErrors24h: number; lastSnapshotAt: string | null };
}

type Period = 'month' | 'quarter' | 'year';

function periodRange(today: string, p: Period) {
  if (p === 'month') return { from: monthStart(today), to: monthEnd(today) };
  if (p === 'quarter') {
    const m = Number(today.slice(5, 7));
    const qStart = `${today.slice(0, 4)}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
    return { from: qStart, to: monthEnd(addMonths(qStart, 2)) };
  }
  return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
}

/**
 * Start page (launchpad). Everything on it follows the rights and the role of the person: shortcut tiles for the
 * things they do, "Meine Aufgaben" (what waits for them), announcements, what is coming up, own figures, and for
 * leading roles the notes about approvals, budgets and billing.
 */
/** Blocks of the start page and their width on large screens (3 columns). */
const BLOCKS = ['tiles', 'work', 'announcements', 'week', 'upcoming', 'figures', 'lead'] as const;
type BlockKey = (typeof BLOCKS)[number];
const BLOCK_SPAN: Record<BlockKey, string> = {
  tiles: 'xl:col-span-3',
  work: 'xl:col-span-2',
  week: 'xl:col-span-2',
  announcements: 'xl:col-span-1',
  upcoming: 'xl:col-span-1',
  figures: 'xl:col-span-3',
  lead: 'xl:col-span-3',
};
type OverviewLayout = { order: string[]; hidden: string[] };

/** The person's own arrangement of the start page (item 16), stored with the account so it follows to every device. */
function useOverviewLayout() {
  const q = useQ('prefs.get', { keys: ['layout.overview'] });
  const set = useM('prefs.set', { invalidate: ['prefs.'] });
  const layout: OverviewLayout = (q.data?.['layout.overview'] as OverviewLayout | undefined) ?? { order: [], hidden: [] };
  const order = useMemo(() => {
    const known = layout.order.filter((k): k is BlockKey => (BLOCKS as readonly string[]).includes(k));
    return [...known, ...BLOCKS.filter((b) => !known.includes(b))];
  }, [layout.order]);
  return { order, hidden: new Set(layout.hidden), save: (v: OverviewLayout) => set.mutateAsync({ key: 'layout.overview', value: v }), saving: set.isPending };
}

function LayoutDialog({
  open,
  onClose,
  order: initialOrder,
  hidden: initialHidden,
  tiles,
  available,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  order: BlockKey[];
  hidden: Set<string>;
  tiles: Tile[];
  available: Set<BlockKey>;
  onSave: (v: OverviewLayout) => Promise<unknown>;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [order, setOrder] = useState(initialOrder);
  const [hidden, setHidden] = useState(new Set(initialHidden));
  const toggle = (k: string, on: boolean) => {
    const next = new Set(hidden);
    if (on) next.delete(k);
    else next.add(k);
    setHidden(next);
  };
  const move = (i: number, dir: -1 | 1) => {
    const next = [...order];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('overview.layout.title')}
      footer={
        <>
          <Button onClick={() => (setOrder([...BLOCKS]), setHidden(new Set()))}>{t('overview.layout.reset')}</Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() =>
              onSave({ order, hidden: [...hidden] })
                .then(() => (toast.ok(t('common.saved')), onClose()))
                .catch(toast.error)
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-graphite">{t('overview.layout.hint')}</p>
      <ul className="flex flex-col gap-1">
        {order
          .filter((k) => available.has(k))
          .map((k) => {
            const i = order.indexOf(k);
            return (
              <li key={k} className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-1.5">
                <Checkbox label={t(`overview.layout.blocks.${k}`)} checked={!hidden.has(k)} onChange={(e) => toggle(k, e.target.checked)} />
                <span className="flex">
                  <IconButton label={t('overview.layout.up')} icon={<ChevronUp className="size-4" />} disabled={i === 0} onClick={() => move(i, -1)} />
                  <IconButton label={t('overview.layout.down')} icon={<ChevronDown className="size-4" />} disabled={i === order.length - 1} onClick={() => move(i, 1)} />
                </span>
              </li>
            );
          })}
      </ul>
      <h3 className="mt-4 mb-1 text-sm font-semibold">{t('overview.layout.tiles')}</h3>
      <div className="grid gap-1 sm:grid-cols-2">
        {tiles
          .filter((x) => x.key !== 'book')
          .map((x) => (
            <Checkbox key={x.key} label={x.label} checked={!hidden.has(`tile:${x.key}`)} onChange={(e) => toggle(`tile:${x.key}`, e.target.checked)} />
          ))}
      </div>
    </Modal>
  );
}

export function OverviewPage() {
  const { t } = useTranslation();
  const { me, can } = useMe();
  const q = useQ('dashboard.get', {});
  const home = useQ('home.get', {}, { refetchInterval: 120_000 });
  const { groups, badges } = useNav();
  const [book, setBook] = useState(false);
  const layout = useOverviewLayout();
  const [arranging, setArranging] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const data = q.data as unknown as Dashboard;
  const m = data.me;
  const employee = me.person.kind === 'employee';
  const leader = !!(data.approvals || data.budgetAlerts || data.formaWarnings || data.overPlan || data.billing || data.system || me.cockpit);
  const has = (to: string) => !!findTab(groups, to);
  const icon = 'size-4';
  const tiles: Tile[] = [
    { key: 'book', icon: <Plus className={icon} />, label: t('entry.newTitle'), onClick: () => setBook(true), primary: true },
    { key: 'week', icon: <CalendarDays className={icon} />, label: t('nav.week'), to: '/week', value: m.hasTarget ? `${h(m.thisWeekMinutes)} / ${hh(m.thisWeekCapacity)}` : hh(m.thisWeekMinutes) },
    {
      key: 'entries',
      icon: <ClipboardList className={icon} />,
      label: t('nav.entries'),
      to: '/entries',
      value: badges.rejected ? t('home.tiles.toCorrect', { count: badges.rejected }) : undefined,
      tone: badges.rejected ? 'bad' : undefined,
    },
    ...(has('/my-absences') ? [{ key: 'absence', icon: <CalendarCheck className={icon} />, label: t('home.tiles.absence'), to: '/my-absences' } satisfies Tile] : []),
    ...(has('/approvals') ? [{ key: 'approvals', icon: <CheckCheck className={icon} />, label: t('nav.approvals'), to: '/approvals', value: badges.approvals ? t('home.tiles.openCount', { count: badges.approvals }) : undefined, tone: badges.approvals ? ('warn' as const) : undefined } satisfies Tile] : []),
    ...(has('/leave-requests') ? [{ key: 'leave', icon: <CalendarClock className={icon} />, label: t('nav.leaveRequests'), to: '/leave-requests', value: badges.leave ? t('home.tiles.openCount', { count: badges.leave }) : undefined, tone: badges.leave ? ('warn' as const) : undefined } satisfies Tile] : []),
    ...(has('/planning') ? [{ key: 'planning', icon: <CalendarRange className={icon} />, label: t('nav.planning'), to: '/planning' } satisfies Tile] : []),
    ...(has('/cockpit') ? [{ key: 'cockpit', icon: <PieChart className={icon} />, label: t('nav.cockpit'), to: '/cockpit' } satisfies Tile] : []),
    ...(has('/billing') ? [{ key: 'billing', icon: <ReceiptText className={icon} />, label: t('nav.billing'), to: '/billing', value: data.billing?.draftDocs ? t('overview.draftDocs', { count: data.billing.draftDocs }) : undefined } satisfies Tile] : []),
    ...(has('/customers') ? [{ key: 'customers', icon: <Contact className={icon} />, label: t('nav.customers'), to: '/customers' } satisfies Tile] : []),
    ...(can('announcements.manage') ? [{ key: 'publish', icon: <Megaphone className={icon} />, label: t('home.tiles.publish'), to: '/announcements?tab=manage&new=1' } satisfies Tile] : []),
  ];
  // the blocks this person can have (figures for employees, notes for those who lead) in the chosen order
  const available = new Set<BlockKey>(BLOCKS.filter((b) => (b !== 'figures' || employee) && (b !== 'lead' || leader)));
  const visibleTiles = tiles.filter((x) => x.key === 'book' || !layout.hidden.has(`tile:${x.key}`));
  const block = (k: BlockKey) => {
    switch (k) {
      case 'tiles':
        return <TileGrid tiles={visibleTiles} />;
      case 'work':
        return <MyWork m={m} home={home.data} />;
      case 'week':
        return <MyWeek m={m} />;
      case 'announcements':
        return home.data ? <AnnouncementsPanel data={home.data} /> : home.error ? <ErrorBox error={home.error} /> : null;
      case 'upcoming':
        return home.data ? <UpcomingPanel items={home.data.upcoming} today={me.today} /> : null;
      case 'figures':
        return (
          <>
            <SectionTitle>{t('overview.mine')}</SectionTitle>
            <MyFigures />
          </>
        );
      case 'lead':
        return (
          <>
            <SectionTitle
              action={
                me.cockpit && (
                  <Link to="/cockpit">
                    <Button size="sm" icon={<PieChart className="size-3.5" />}>
                      {t('overview.toCockpit')}
                    </Button>
                  </Link>
                )
              }
            >
              {t('overview.lead')}
            </SectionTitle>
            <LeaderNotes data={data} />
          </>
        );
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('overview.hello', { name: me.person.displayName })}</h1>
          <p className="text-sm text-graphite">
            {dLong(me.today)} · {t('common.cw')} {kw(me.today)}
          </p>
        </div>
        <Button size="sm" icon={<LayoutGrid className="size-3.5" />} onClick={() => setArranging(true)}>
          {t('overview.layout.button')}
        </Button>
      </div>

      <div className="grid items-start gap-4 [grid-auto-flow:row_dense] xl:grid-cols-3">
        {layout.order
          .filter((k) => available.has(k) && !layout.hidden.has(k))
          .map((k) => (
            <div key={k} className={clsx('flex min-w-0 flex-col gap-4', BLOCK_SPAN[k])}>
              {block(k)}
            </div>
          ))}
      </div>
      {arranging && (
        <LayoutDialog
          open
          onClose={() => setArranging(false)}
          order={layout.order}
          hidden={layout.hidden}
          tiles={tiles}
          available={available}
          onSave={layout.save}
          saving={layout.saving}
        />
      )}
      <EntryDialog open={book} onClose={() => setBook(false)} defaultDate={me.today} />
    </div>
  );
}

function SectionTitle({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="mt-2 flex items-center gap-3">
      <h2 className="text-[13px] font-semibold tracking-wide text-graphite uppercase">{children}</h2>
      <span className="h-px flex-1 bg-line" />
      {action}
    </div>
  );
}

function MyWeek({ m }: { m: Dashboard['me'] }) {
  const { t } = useTranslation();
  const { me } = useMe();
  // time off in lieu is part of the target but not expected to be booked
  const rest = Math.max(0, m.thisWeekCapacity - m.thisWeekCompensation - m.thisWeekMinutes);
  return (
    <Panel
      title={t('overview.myWeekTitle', { week: kw(m.weekStart) })}
      actions={
        <Link to="/week">
          <Button size="sm" icon={<ArrowRight className="size-3.5" />}>
            {t('overview.toWeek')}
          </Button>
        </Link>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="min-w-48 flex-1">
          <div className="num text-2xl font-semibold">
            {m.hasTarget ? (
              <>
                {h(m.thisWeekMinutes)} <span className="text-base font-normal text-graphite">/ {hh(m.thisWeekCapacity)}</span>
              </>
            ) : (
              hh(m.thisWeekMinutes)
            )}
          </div>
          {m.hasTarget && <Gauge value={m.thisWeekMinutes} capacity={m.thisWeekCapacity} comp={m.thisWeekCompensation} className="mt-1.5" />}
        </div>
        {m.hasTarget && (
          <span className="num text-sm text-graphite">
            {rest > 0 ? t('overview.remaining', { hours: h(rest) }) : t('overview.weekFull')}
            {m.thisWeekCompensation > 0 && <span className="block text-xs">{t('week.compHint', { hours: h(m.thisWeekCompensation) })}</span>}
          </span>
        )}
      </div>
      <WeekBars
        height={140}
        showCapacity={m.hasTarget}
        head={t('common.date')}
        data={m.days.map((x) => ({
          label: `${wd(x.date)} ${d(x.date).slice(0, 6)}`,
          axis: wd(x.date),
          booked: x.bookedMinutes,
          billable: x.billableMinutes,
          capacity: x.capacityMinutes,
          comp: x.compensationMinutes,
          current: x.date === me.today,
          note: x.holiday ?? (x.absenceKind ? t(`week.absence.${x.absenceKind as 'vacation'}`) : undefined),
        }))}
      />
    </Panel>
  );
}

/**
 * "Meine Aufgaben": what waits for the person. Two sources, one list: the server lists what needs action (rejected
 * hours, missing bookings, approvals, absences without a document, ...: the same rules as the notification panel), and
 * the page adds hints about the own hours that only the weekly figures show.
 * Colour: red = action needed, amber = to check, green = positive.
 */
function MyWork({ m, home }: { m: Dashboard['me']; home: HomeData | undefined }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const past = m.weeks.filter((w) => w.state === 'under' || w.state === 'over').slice(-6).reverse();
  type Item = { key: string; tone: 'bad' | 'warn' | 'good' | 'neutral'; icon?: React.ReactNode; text: string; detail?: string | null; to?: string };
  const work = home?.work ?? [];
  const covered = (prefix: string) => work.some((w) => w.code.startsWith(prefix));
  const items: Item[] = [];
  for (const w of work) {
    const { text, detail } = notifyText(t, w);
    const bad = w.severity === 'critical' || w.code === 'hours.rejected' || w.code === 'leave.change_requested' || w.code === 'leave.document_missing';
    items.push({
      key: w.key,
      tone: bad ? 'bad' : w.severity === 'warning' ? 'warn' : 'neutral',
      icon: <span className={`mt-px inline-flex size-5 shrink-0 items-center justify-center rounded-full ${CATEGORY_STYLE[w.category].tone}`}>{CATEGORY_STYLE[w.category].icon}</span>,
      text,
      detail,
      to: w.link,
    });
  }
  // hints from the weekly figures (the server list does not repeat them)
  for (const w of past.filter((x) => x.state === 'under')) {
    items.push({
      key: `u${w.weekStart}`,
      tone: 'bad',
      icon: <ChevronDown className="size-4 shrink-0" />,
      text: t('overview.weekUnder', { week: kw(w.weekStart), hours: h(w.capacityMinutes - w.compensationMinutes - w.bookedMinutes) }),
      to: `/week?w=${w.weekStart}`,
    });
  }
  // open drafts: only when the notification rules (deadline of the week) do not already report them
  if (!covered('missing.week') && m.lastWeekOpen > 0) items.push({ key: 'open', tone: 'warn', text: t('overview.openLastWeek', { count: m.lastWeekOpen }), to: `/week?w=${addDays(m.weekStart, -7)}` });
  for (const w of past.filter((x) => x.state === 'over')) {
    items.push({
      key: `o${w.weekStart}`,
      tone: 'warn',
      icon: <ChevronUp className="size-4 shrink-0" />,
      text: t('overview.weekOver', { week: kw(w.weekStart), hours: h(w.bookedMinutes - w.capacityMinutes) }),
      to: `/week?w=${w.weekStart}`,
    });
  }
  if (!covered('missing.week') && m.olderOpen > 0) items.push({ key: 'older', tone: 'neutral', text: t('overview.openOlder', { count: m.olderOpen }), to: '/entries' });
  for (const f of m.fastTasks) {
    items.push({
      key: `f${f.taskId}`,
      tone: 'good',
      icon: <Sparkles className="size-4 shrink-0" />,
      text: t('overview.fastTask', { ref: f.ref, title: f.title, plan: h(f.planShareMinutes, 1), own: h(f.ownMinutes, 1) }),
    });
  }
  const needsAction = items.some((i) => i.tone !== 'good');
  const extreme = m.fastTasks.some((f) => f.ownMinutes > 0 && f.planShareMinutes / f.ownMinutes >= 4);
  const tone = { bad: 'bg-bad-soft text-bad', warn: 'bg-warn-soft text-warn', good: 'bg-good-soft text-good', neutral: 'bg-sheet-2 text-ink' };
  return (
    <Panel title={t('home.myWork')}>
      {!needsAction && (
        <div className="mb-2 flex items-center gap-2 text-sm text-good">
          <CircleCheck className="size-4" /> {t('overview.allGood')}
        </div>
      )}
      {items.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {items.map((r) => {
            const body = (
              <span className={clsx('flex items-start gap-2 rounded-md px-2.5 py-1.5 text-sm', tone[r.tone])}>
                {r.icon}
                <span className="min-w-0">
                  {r.text}
                  {r.detail && <span className="mt-0.5 block text-xs italic opacity-80">„{r.detail}“</span>}
                </span>
              </span>
            );
            return <li key={r.key}>{r.to ? <Link to={r.to}>{body}</Link> : body}</li>;
          })}
        </ul>
      )}
      {extreme && <p className="mt-2 text-xs text-graphite">{t('overview.fastCheck')}</p>}
      <p className="mt-3 text-xs text-graphite">{t('overview.stateHint', { hours: h(me.settings.weekToleranceMinutes, 0) })}</p>
    </Panel>
  );
}

function MyFigures() {
  const { t } = useTranslation();
  const { me } = useMe();
  const [period, setPeriod] = useState<Period>('month');
  const [all, setAll] = useState(false);
  const r = periodRange(me.today, period);
  const q = useQ('kpi.me', r);
  const k = q.data?.kpi;
  const eff = q.data?.efficiency;
  const coverageText = useCoverageText();
  return (
    <Panel
      title={t('overview.myFigures')}
      actions={
        <div className="flex gap-1">
          {(['month', 'quarter', 'year'] as const).map((p) => (
            <Button key={p} size="sm" variant={period === p ? 'primary' : 'secondary'} onClick={() => setPeriod(p)}>
              {t(`overview.periods.${p}`)}
            </Button>
          ))}
        </div>
      }
    >
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {k && q.data && eff && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            {q.data.hasTarget ? (
              <>
                <Stat label={t('overview.targetActual')} value={`${h(k.bookedMinutes, 1)} / ${h(k.capacityMinutes, 1)} h`} hint={t('kpi.asOf', { from: d(q.data.since), to: d(q.data.asOf) })} />
                <Stat
                  label={t('kpi.balance')}
                  value={`${k.balanceMinutes > 0 ? '+' : ''}${hh(k.balanceMinutes, 1)}`}
                  hint={t('overview.weekStates', { under: q.data.underWeeks, over: q.data.overWeeks })}
                  tone={q.data.underWeeks > 0 ? 'bad' : q.data.overWeeks > 0 ? 'warn' : undefined}
                />
                <Stat label={t('kpi.utilization')} value={pct(k.utilization)} hint={t('kpi.utilizationHint')} />
              </>
            ) : (
              <Stat label={t('kpi.booked')} value={hh(k.bookedMinutes, 1)} hint={t('kpi.asOf', { from: d(q.data.since), to: d(q.data.asOf) })} />
            )}
            <Stat label={t('kpi.billableShare')} value={pct(k.billableShare)} hint={hh(k.billableMinutes, 1)} />
            <Stat
              label={t('kpi.efficiency')}
              value={eff.tasks ? pct(eff.ratio) : '-'}
              hint={eff.tasks ? t('kpi.efficiencyHint', { count: eff.tasks, plan: h(eff.earnedMinutes, 1), actual: h(eff.actualMinutes, 1) }) : t('kpi.efficiencyNone')}
              tone={eff.tasks ? efficiencyTone(eff.ratio) : undefined}
            />
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('kpi.weekly')}</h3>
              <WeekBars
                showCapacity={q.data.hasTarget}
                data={q.data.weeks.map((w) => {
                  const n = isoWeek(w.weekStart).week;
                  return {
                    label: `${t('kpi.weekPrefix')} ${n}`,
                    axis: `${n}`,
                    booked: w.bookedMinutes,
                    billable: w.billableMinutes,
                    capacity: w.capacityMinutes,
                    comp: w.compensationMinutes,
                    state: w.state,
                    note: w.compensationMinutes ? t('week.compHint', { hours: h(w.compensationMinutes) }) : undefined,
                  };
                })}
              />
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('kpi.byProject')}</h3>
              <HBars head={t('common.project')} rows={q.data.byProject.slice(0, 8).map((p) => ({ label: `${p.number} ${p.name}`, value: p.minutes, billable: p.billable }))} />
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('areas.titleMine')}</h3>
              <AreaBars areas={q.data.areas} hint={t('areas.hintMine')} />
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('hourTypes.title')}</h3>
              <HourTypeBars types={q.data.hourTypes} />
            </div>
          </div>
          <HourTerms />
          {(eff.items.length > 0 || eff.sharedTasks > 0 || eff.unplannedMinutes > 0) && (
            <div>
              <h3 className="mb-1 text-sm font-semibold">{t('overview.planVsActual')}</h3>
              <p className="mb-2 text-xs text-graphite">
                {t('kpi.efficiencyExplainOwn')}
                {eff.sharedTasks > 0 && ` ${t('kpi.sharedLeftOut', { count: eff.sharedTasks })}`}
                {eff.unplannedMinutes > 0 && ` ${coverageText(eff)}`}
              </p>
              {eff.items.length > 0 && <EfficiencyTable items={all ? eff.items : eff.items.slice(0, 5)} />}
              {eff.items.length > 5 && (
                <Button size="sm" variant="ghost" className="mt-1" onClick={() => setAll(!all)}>
                  {all ? t('overview.showLess') : t('overview.showAll', { count: eff.items.length })}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

export interface EfficiencyItemView {
  taskId: string;
  ref: string;
  title: string;
  projectNumber: string;
  doneOn: string;
  plannedMinutes: number;
  /** all hours on the task: only in the confidential evaluation, never in the own view */
  taskMinutes?: number;
  personMinutes: number;
  earnedMinutes: number;
  ratio: number;
}

/**
 * Finished tasks against plan. Own view: tasks booked alone (plan, own hours, factor). Evaluation of
 * management: plan, all hours on the task, hours of the person, factor (plan / all hours).
 */
export function EfficiencyTable({ items }: { items: EfficiencyItemView[] }) {
  const { t } = useTranslation();
  const team = items.some((i) => i.taskMinutes !== undefined);
  return (
    <div className="scroll-x rounded-md border border-line">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="border-b border-line bg-sheet-2">
          <tr>
            <th className={th}>{t('common.task')}</th>
            <th className={th}>{t('overview.doneOn')}</th>
            <th className={clsx(th, 'text-right')}>{t('overview.planTask')}</th>
            {team && <th className={clsx(th, 'text-right')}>{t('overview.actualTask')}</th>}
            <th className={clsx(th, 'text-right')}>{team ? t('overview.personHours') : t('overview.ownHours')}</th>
            <th className={th}>{t('overview.factor')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.taskId} className="border-b border-line/60 last:border-0">
              <td className={td}>
                <span className="num font-semibold text-accent">{i.ref}</span> {i.title}
                <span className="block text-xs text-graphite">{i.projectNumber}</span>
              </td>
              <td className={clsx(td, 'num whitespace-nowrap')}>{d(i.doneOn)}</td>
              <td className={clsx(td, 'num text-right')}>{h(i.plannedMinutes, 1)}</td>
              {team && <td className={clsx(td, 'num text-right')}>{h(i.taskMinutes ?? 0, 1)}</td>}
              <td className={clsx(td, 'num text-right')}>{h(i.personMinutes, 1)}</td>
              <td className={td}>
                <span className="flex items-center gap-2">
                  <PlanBar planned={i.plannedMinutes} booked={i.taskMinutes ?? i.personMinutes} />
                  <Pill tone={efficiencyTone(i.ratio) ?? 'neutral'}>{pct(i.ratio)}</Pill>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeaderNotes({ data }: { data: Dashboard }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {data.approvals && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              <CheckCheck className="size-4 text-accent" /> {t('overview.approvals')}
            </span>
          }
          actions={
            <Link to="/approvals">
              <Button size="sm">{t('overview.toApprovals')}</Button>
            </Link>
          }
        >
          <div className="num text-3xl font-semibold">{data.approvals.total}</div>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <Pill tone="info">
              {t('overview.approvalsL1')}: {data.approvals.l1}
            </Pill>
            <Pill tone="accent">
              {t('overview.approvalsFinal')}: {data.approvals.final}
            </Pill>
          </div>
        </Panel>
      )}
      {data.budgetAlerts && (
        <Panel title={t('overview.budget')} bodyClassName="p-0">
          <ul>
            {data.budgetAlerts.slice(0, 5).map((b) => (
              <li key={b.id} className="border-b border-line/70 px-4 py-2 last:border-0">
                <Link to={`/projects/${b.id}`} className="flex flex-col gap-1.5 hover:text-accent">
                  <span className="flex justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate">
                      <span className="num font-medium">{b.number}</span> {b.name}
                    </span>
                    <span className={b.bookedMinutes > b.budgetMinutes ? 'num font-semibold text-bad' : 'num font-semibold text-warn'}>{pct(b.bookedMinutes / b.budgetMinutes)}</span>
                  </span>
                  <Gauge value={b.bookedMinutes} capacity={b.budgetMinutes} />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {data.overPlan && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-warn" /> {t('overview.overPlanTitle', { count: data.overPlan.count })}
            </span>
          }
          bodyClassName="p-0"
        >
          <ul>
            {data.overPlan.tasks.map((x) => (
              <li key={x.id} className="border-b border-line/70 px-4 py-2 last:border-0">
                <Link to={`/planning?project=${x.projectId}&tab=tasks`} className="flex flex-col gap-1.5 hover:text-accent">
                  <span className="flex justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate">
                      <span className="num">{x.projectNumber}</span> <span className="num font-semibold text-accent">{x.ref}</span> {x.title}
                    </span>
                    <span className="num font-semibold whitespace-nowrap text-warn">
                      {h(x.bookedMinutes, 1)} / {h(x.plannedMinutes, 1)}
                    </span>
                  </span>
                  <PlanBar planned={x.plannedMinutes} booked={x.bookedMinutes} />
                </Link>
              </li>
            ))}
          </ul>
          <p className="px-4 py-2 text-xs text-graphite">{t('overview.overPlanHint')}</p>
        </Panel>
      )}
      {data.formaWarnings && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-warn" /> {t('overview.forma')}
            </span>
          }
        >
          <p className="mb-2 text-sm text-graphite">{t('overview.formaText')}</p>
          <ul className="flex flex-col gap-1.5 text-sm">
            {data.formaWarnings.projects.map((p) => (
              <li key={p.id}>
                <Link to={`/projects/${p.id}`} className="hover:text-accent">
                  <span className="num font-medium">{p.number}</span> {p.name} <Pill tone="warn">{t(`projects.formaState.${p.state as 'deleted'}`)}</Pill>
                </Link>
              </li>
            ))}
            {data.formaWarnings.tasks.slice(0, 6).map((x) => (
              <li key={x.id}>
                <Link to={`/projects/${x.projectId}`} className="hover:text-accent">
                  <span className="num">{x.projectNumber}</span> <span className="num font-semibold text-accent">{x.ref}</span> {x.title}{' '}
                  <Pill tone="warn">{x.state === 'deleted' ? t('task.formaDeleted') : t('task.formaAccessLost')}</Pill>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {data.billing && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              <ReceiptText className="size-4 text-accent" /> {t('overview.billing')}
            </span>
          }
          actions={
            <Link to="/billing">
              <Button size="sm">{t('common.open')}</Button>
            </Link>
          }
        >
          <ul className="flex flex-col gap-1 text-sm">
            <li>{t('overview.draftDocs', { count: data.billing.draftDocs })}</li>
            <li className="num">{t('overview.unbilled', { hours: h(data.billing.unbilledMinutes, 1) })}</li>
          </ul>
        </Panel>
      )}
      {data.system && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              <Server className="size-4 text-graphite" /> {t('overview.system')}
            </span>
          }
        >
          <ul className="flex flex-col gap-1 text-sm">
            <li className={data.system.syncErrors24h ? 'text-bad' : ''}>{data.system.syncErrors24h ? t('overview.syncErrors', { count: data.system.syncErrors24h }) : t('overview.syncOk')}</li>
            <li>{t('overview.lastSnapshot', { when: dt(data.system.lastSnapshotAt) })}</li>
          </ul>
        </Panel>
      )}
      {!data.approvals && !data.budgetAlerts && !data.formaWarnings && !data.overPlan && !data.billing && !data.system && <Notice tone="neutral">{t('overview.leadQuiet')}</Notice>}
    </div>
  );
}
